import { friendSummary } from "../Components/GoingOut/goingOutLabels";
import type { AppNotification } from "../data/types";
import { profilePath, venuePath } from "./routes";

export type NotificationCopy = {
  /** Vem eller vad som utlöste notisen. */
  actor: string;
  action: string;
  subject?: string;
  meta: string;
  /** Bild att visa i notisen — albumomslag för låtar, avatar för personer. */
  imageUrl: string | null;
  /** Fallback när det inte finns någon bild. */
  initialsFrom: string;
  /** Vart notisen leder vid klick. */
  href: string;
};

export function notificationCopy(n: AppNotification): NotificationCopy {
  const actorName = n.actor?.displayName ?? "Someone";
  const venueName = n.venue?.name ?? "a venue";
  const profileHref = n.actor ? profilePath(n.actor.id) : "/";

  // Notiser som handlar om en plats landar på stället, inte på en profil —
  // det är stället man vill se.
  const venueHref = n.venue ? venuePath(n.venue.id) : "/";

  switch (n.type) {
    case "FOLLOW_REQUEST":
      return {
        actor: actorName,
        action: "wants to follow you",
        meta: "Follow request",
        imageUrl: n.actor?.avatarUrl ?? null,
        initialsFrom: actorName,
        href: profileHref,
      };

    case "FOLLOW_ACCEPTED":
      return {
        actor: actorName,
        action: "accepted your follow request",
        meta: `You are now following ${actorName}`,
        imageUrl: n.actor?.avatarUrl ?? null,
        initialsFrom: actorName,
        href: profileHref,
      };

    case "CHECK_IN":
      return {
        actor: actorName,
        action: "checked in at",
        subject: venueName,
        meta: "Friend activity",
        imageUrl: n.actor?.avatarUrl ?? null,
        initialsFrom: actorName,
        href: venueHref,
      };

    case "ON_MY_WAY":
      return {
        actor: actorName,
        action: "is on the way to",
        subject: venueName,
        meta: "On my way",
        imageUrl: n.actor?.avatarUrl ?? null,
        initialsFrom: actorName,
        href: venueHref,
      };

    case "ON_MY_WAY_ARRIVED":
      return {
        actor: actorName,
        action: "arrived at",
        subject: venueName,
        meta: "They made it",
        imageUrl: n.actor?.avatarUrl ?? null,
        initialsFrom: actorName,
        href: venueHref,
      };

    case "POST_LIKE":
      return {
        actor: actorName,
        action: "liked your post",
        subject: n.track?.title ?? undefined,
        meta: n.track?.artist ?? "Someone liked your post",
        // Omslaget säger vilket inlägg det gäller. Saknas det får
        // avataren stå för igenkänningen i stället.
        imageUrl: n.track?.albumCover ?? n.actor?.avatarUrl ?? null,
        initialsFrom: actorName,
        href: "/feed",
      };

    case "FRIENDS_GOING_OUT": {
      // Aggregerad: den har ingen actor, för den handlar om en grupp och
      // inte om en person. Talen är de som gällde när notisen skapades
      // och räknas aldrig om — annars skulle gårdagens notis säga
      // "0 friends" i morgon bitti.
      const group = n.group ?? { out: 0, maybe: 0 };

      return {
        actor: "Tonight",
        // Samma funktion som raden på kartan och som pushens text. Att de
        // tre skulle kunna säga olika saker om samma kväll är precis vad
        // en gemensam formulering finns för att förhindra.
        action:
          friendSummary({ ...group, total: group.out + group.maybe }) ??
          "Nobody is out tonight",
        meta: "Friends going out",
        imageUrl: null,
        initialsFrom: "Tonight",
        // Flödet är det enda stället som visar vilka de är, och med
        // vilket läge. Notisen säger antalet, sektionen säger resten.
        href: "/feed",
      };
    }

    case "VENUE_TRACK":
    default:
      return {
        actor: venueName,
        action: "is playing",
        subject: n.track?.title ?? "a new track",
        meta: n.track?.artist ?? "Live music update",
        imageUrl: n.track?.albumCover ?? null,
        initialsFrom: venueName,
        href: venueHref,
      };
  }
}
